/**
 * Anonymous 60s "pressure + glimpse" preview — illustrative only, not your full Career DNA report.
 */

export const TRY_INTRO = {
  studio: 'ChooseRightCareer presents',
  kicker: 'One-minute preview · Career DNA',
  title: 'See how Career DNA reads your style—in about a minute',
  subtitle:
    'Six quick choices, no sign-up. Get a gentle snapshot of how you tend to think and what kinds of paths might suit you.',
  cta: 'See my preview',
};

/** Full-screen cinematic gradients — one per beat (night → school → social → home → desk → results). */
export const SCENE_BACKDROP_CLASSES = [
  'bg-gradient-to-br from-[#0a0620] via-[#1a1040] to-[#0f172a]',
  'bg-gradient-to-br from-[#0c1222] via-[#162032] to-[#0f172a]',
  'bg-gradient-to-br from-[#0f1724] via-[#134e4a] to-[#0f172a]',
  'bg-gradient-to-br from-[#1c1008] via-[#292524] to-[#0f172a]',
  'bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0c4a6e]',
  'bg-gradient-to-br from-[#18181b] via-[#27272a] to-[#0f172a]',
];

export const TRY_BEATS = [
  {
    id: 'late',
    eyebrow: 'Late night · 11 p.m.',
    title: 'Deadline tomorrow. You’re already drained.',
    choices: [
      { id: 0, line: 'One more hour tonight', sub: 'Might as well push' },
      { id: 1, line: 'Sleep now, start fresh', sub: 'Better with a clear head' },
    ],
  },
  {
    id: 'group',
    eyebrow: 'Group project',
    title: 'One person didn’t show up. The teacher asks how it’s going.',
    choices: [
      { id: 0, line: 'Cover it as a team for now', sub: 'Keep things smooth' },
      { id: 1, line: 'Call it out, split the work', sub: 'Fair, even if awkward' },
    ],
  },
  {
    id: 'plans',
    eyebrow: 'Same evening',
    title: 'Friends want to go out. You still have prep left.',
    choices: [
      { id: 0, line: 'Go out for a bit, then focus', sub: 'Take a break, then get back' },
      { id: 1, line: 'Skip tonight, finish prep first', sub: 'Get it done, then relax' },
    ],
  },
  {
    id: 'parent',
    eyebrow: 'Dinner table',
    title: '“Take the safe stream.” You’re interested in something else.',
    choices: [
      { id: 0, line: 'Explain your side with a plan', sub: 'Show them you’ve thought it through' },
      { id: 1, line: 'Stay quiet, talk later', sub: 'Wait for a better moment' },
    ],
  },
  {
    id: 'stuck',
    eyebrow: 'Study block',
    title: 'You are stuck on the same problem for 20 minutes.',
    choices: [
      { id: 0, line: 'Switch topics, come back later', sub: 'Clear your head first' },
      { id: 1, line: 'Stay with it till it clicks', sub: 'Keep pushing through' },
    ],
  },
  {
    id: 'results',
    eyebrow: 'When scores land',
    title: 'Didn’t go well. What’s your first instinct?',
    choices: [
      { id: 0, line: 'Figure out what went wrong', sub: 'Break it down, learn from it' },
      { id: 1, line: 'Move on and focus ahead', sub: 'Don’t dwell too much' },
    ],
  },
];

/** 0 = first choice, 1 = second choice per beat → index into teaser lines */
export function teaserIndexFromChoices(choices) {
  if (!choices?.length) return 0;
  let n = 0;
  for (let i = 0; i < choices.length; i += 1) {
    n += (choices[i] & 1) << i;
  }
  return n % TEASER_LINES.length;
}

export const TEASER_LINES = [
  'Under pressure, you protect momentum—even when it costs sleep or a harder conversation later.',
  'You read rooms fast: you’ll absorb short-term awkwardness if it keeps the group moving.',
  'You recharge through people, then lock in hard—social battery first, deep work second.',
  'When stakes rise with family, you lead with proof: examples, plans, and a calmer second pass.',
  'If something won’t unlock, you’d rather change the angle than keep head-butting the same wall.',
  'After a miss, you want a clean story of what broke—so the next attempt isn’t guesswork.',
  'You can carry silence like a strategy: you pick when to spend your truth, not if.',
  'You’re willing to trade tonight’s comfort if it buys you a clearer tomorrow morning.',
  'You bias toward fairness in groups—even when it means being the person who says the uncomfortable line.',
  'You’d rather reset with sleep and a sprint than romanticize an all-nighter.',
  'You treat “stuck” as a signal to rotate inputs, not as a verdict on your ability.',
  'You recover forward: less spiral, more “what’s the next concrete move?”',
];

export const TRY_REVEAL = {
  docTitle: 'Career DNA · Sample extract',
  docMeta: 'Illustrative preview only—not your full assessment report',
  redactedHint: 'Your full report explores 7 more dimensions in depth',
  footerLine: 'The complete Career DNA read adds 7 more structured dimensions with guidance you can use with family.',
  ctaPrimary: 'Know your Career DNA',
  ctaSecondary: 'Play again',
  exitHome: 'Back to home',
};
